import firebase from 'firebase/app';
import React, { useState, useMemo } from 'react';
import { DialogContent, Button, DialogActions, Typography } from '@material-ui/core';
import MaterialTable from '../../../../../customComponents/materialTable/Table';
import AppFunctionsService from '../../../../../../services/appFunctionsService';
import { Warning } from '@material-ui/icons';
import ConfirmDialog from '../../../../../customComponents/dialog/ConfirmDialog';
import { EmployeeHelper, IUserSession } from '../../../../../../@types/employee';
import lodash from 'lodash';
import TransformerMunicipalityReadingHelper, { ITransformerMunicipalityReading } from '../../../../../../@types/model/transformer/reading/transformerMunicipalityReading';
import ProjectDialog from '../../../../../customComponentV2/dialog/ProjectDialog';
import { IMeterReadingsView } from '../../../../../../@types/model/transformer/reading/transformerReading';

interface IMunicipalityApproveDialogProps {
    open : boolean;
    disapprove ?: boolean;
    onClose : (result : boolean) => void;
    values : Array<IMeterReadingsView>;
    title : string;
    readings : Array<ITransformerMunicipalityReading>;

    session : IUserSession;
}

const MunicipalityApproveDialog = (props : IMunicipalityApproveDialogProps) : React.ReactElement => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const onClose = (result : boolean) : void => {
        if (isLoading) return;
        props.onClose(result);
    };

    const onDialogClose = () : void => {
        if (isLoading) return;
        onClose(false);
    };

    const onCloseClick = () : void => {
        if (isLoading) return;
        onClose(false);
    };

    const save = async () : Promise<void> => {
        setIsLoading(true);

        const session = props.session;

        const disapprove = !!props.disapprove;
        const meterReadings = getFilteredData;
        const guids = lodash.uniq(meterReadings.map(x => x.Guid));
        const readings = props.readings.filter(x => guids.includes(x.guid));

        meterReadings.forEach((meterReading) => {
            const readingIndex = readings.findIndex(x => x.guid === meterReading.Guid);
            if (readingIndex < 0) return;

            const reading = readings[readingIndex];
            const meterIndex = reading.transformerMeterNumberReadings.findIndex(x => x.itemNumber === meterReading.ItemNumber);
            if (meterIndex < 0) return;


            const update = {
                ...reading,
                transformerMeterNumberReadings: reading.transformerMeterNumberReadings.map(z => ({
                    ...z,
                })),
            };

            update.transformerMeterNumberReadings[meterIndex].approved = disapprove ? false : true;
            update.transformerMeterNumberReadings[meterIndex].approvedBy = disapprove ? null : EmployeeHelper.doc(session.firebaseUser.uid);
            update.transformerMeterNumberReadings[meterIndex].approvedByName = disapprove ? null : session.firebaseUser.displayName ?? '';
            update.transformerMeterNumberReadings[meterIndex].approvedByEmployee = disapprove ? null : session.employee.EmployeeNumber ?? '';
            update.transformerMeterNumberReadings[meterIndex].approvedOn = disapprove ? null : firebase.firestore.Timestamp.now();

            readings.splice(readingIndex, 1, update);
        });

        try {
            await TransformerMunicipalityReadingHelper.batchReadingSave(readings, session);

            props.onClose(true);
        } finally {
            setIsLoading(false);
        }
    };

    const onConfirm = (result : boolean) : void => {
        setShowConfirm(false);
        if (!result) {
            return;
        }
        save();
    };

    const onOkClick = () : void => {
        if (isLoading) return;

        if (props.disapprove) {
            save();
        } else {
            if (props.values.some(n => !!n.Error)) {
                setShowConfirm(true);
            } else {
                save();
            }
        }
    };

    const getFilteredData : Array<IMeterReadingsView> = useMemo<Array<IMeterReadingsView>>(() => {
        return props.values.filter(x => !props.disapprove ? !x.Approved : x.Approved);
    }, [props.values, props.disapprove]);
    
    return (
        <>
            <ProjectDialog
                isOpen={props.open}
                isLoadingCircular={isLoading}
                onClose={onDialogClose}
                maxWidth={'md'}
                fullWidth
                title={props.title}
            >
                <DialogContent>
                    <div className='fdc'>
                        <MaterialTable<IMeterReadingsView>
                            id='valuesGrid'
                            data={getFilteredData}
                            isLoading={isLoading}
                            rowsPerPage={100}
                            columns={[{
                                header: '',
                                field: 'EPNumber',
                                width: 75,
                                renderCell: row => (
                                    <div className=' fdr aic'>
                                        {
                                            !!row.Error &&
                                            <Warning className={'cy pl10'}/>
                                        }
                                    </div>
                                ),
                            }, {
                                header: 'Date',
                                field: 'Date',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (<span>{AppFunctionsService.formatDateTimeToDateOnly(row.Date)}</span>),
                            }, {
                                header: 'EP Point',
                                field: 'EPNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Area',
                                field: 'Division',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Group',
                                field: 'Group',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Meter',
                                field: 'MeterNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Meter Reading (kWh)',
                                field: 'Reading',
                                width: 200,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Last Reading (kWh)',
                                field: 'Usage',
                                width: 200,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <span>
                                        {
                                            row.Rollover ?
                                                row.Reading - row.Usage + Number(new Array(row.NumberOfDigits).fill('9').join('')) :
                                                row.Reading - row.Usage
                                        }
                                    </span>),
                            }, {
                                header: 'Usage',
                                field: 'Usage',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }]}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseClick} disabled={isLoading} color='primary'>
                        Cancel
                    </Button>
                    <Button type='button' disabled={isLoading} onClick={onOkClick}>
                        <span className='pl10'>SUBMIT</span>
                    </Button>
                </DialogActions>
            </ProjectDialog>
            {
                showConfirm &&
                <ConfirmDialog
                    open
                    onClose={onConfirm}
                    message={'Some readings have errors, are you sure you want to approve them?'}
                    title={'Confirm'} />
            }
        </>
    );
};

export default MunicipalityApproveDialog;